import React, { useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  SimpleGrid,
  Accordion,
  Input,
  Tabs,
} from '@chakra-ui/react';
import { TabList, TabPanels, Tab, TabPanel } from '@/components/ui/chakra-v2-compat';
import { InputGroup, InputLeftElement } from '@/components/ui/input-group';
import { 
  FaArrowLeft, 
  FaSearch,
  FaUserPlus,
  FaCalendar,
  FaComments,
  FaHandshake,
  FaCog,
  FaQuestionCircle,
} from 'react-icons/fa';
import Footer from './Footer';
import SEO from './SEO.jsx';

const Ayuda = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  const faqsCofradias = [
    {
      categoria: 'Primeros Pasos',
      icon: FaUserPlus,
      preguntas: [
        {
          pregunta: '¿Cómo me registro como cofradía?',
          respuesta: 'Haz clic en "Registrarse" en la página principal, selecciona "Cofradía" y completa el formulario con los datos de tu hermandad. Necesitarás proporcionar el CIF, nombre oficial, datos de contacto y persona responsable. Tras el registro, completa tu perfil para aumentar tu visibilidad.',
        },
        {
          pregunta: '¿Necesito validar mi cuenta?',
          respuesta: 'Sí, para que tu perfil sea visible en Comunidad debes validar la cuenta. Completa los datos obligatorios (no hace falta el 100% del perfil): se genera un código que debes enviar por mensaje directo a nuestras redes. El equipo suele revisarlo en 48 horas.',
        },
        {
          pregunta: '¿Qué información debo incluir en mi perfil?',
          respuesta: 'Incluye: nombre oficial de la hermandad, historia, número de hermanos, sede canónica, fotografías de alta calidad (procesiones, túnicas, pasos), localización precisa, eventos anteriores y cualquier información relevante que ayude a las bandas a conocerte mejor.',
        },
      ],
    },
    {
      categoria: 'Gestión de Eventos',
      icon: FaCalendar,
      preguntas: [
        {
          pregunta: '¿Cómo creo un evento procesional?',
          respuesta: 'Ve a "Eventos" → "Crear evento". Completa todos los campos: nombre del evento, fecha, hora, ubicación exacta, duración estimada, número de músicos necesarios y cualquier requisito especial (repertorio, vestimenta, etc.). Cuanto más detallado, mejor.',
        },
        {
          pregunta: '¿Puedo editar un evento después de crearlo?',
          respuesta: 'Sí, puedes editar eventos pendientes desde "Mis Eventos" → "Detalles del evento" → "Editar". Si ya hay conversaciones iniciadas con bandas, estas recibirán una notificación de los cambios realizados.',
        },
        {
          pregunta: '¿Qué pasa si cancelo un evento?',
          respuesta: 'Si cancelas un evento, todas las bandas con las que hayas iniciado conversación recibirán una notificación automática. Deberás proporcionar un motivo de cancelación. Los acuerdos activos se cancelarán automáticamente. El evento aparecerá en tu sección de "Eventos Cancelados".',
        },
      ],
    },
    {
      categoria: 'Buscar y Contactar Bandas',
      icon: FaSearch,
      preguntas: [
        {
          pregunta: '¿Cómo busco bandas para mi evento?',
          respuesta: 'Desde tu evento, haz clic en "Buscar banda". Usa los filtros para encontrar bandas por: tipo (banda de música, cornetas y tambores, etc.), proximidad, disponibilidad en la fecha de tu evento, y valoraciones. Revisa perfiles, escucha audios/vídeos y lee reseñas antes de contactar.',
        },
        {
          pregunta: '¿Cómo contacto con una banda?',
          respuesta: 'En el perfil de la banda, haz clic en "Me interesa" (desde el evento) o "Contactar". Escribe un mensaje personalizado presentando tu hermandad y proporcionando detalles del evento. El mensaje se enviará directamente al chat privado de ese evento.',
        },
        {
          pregunta: '¿Puedo contactar con varias bandas para el mismo evento?',
          respuesta: 'Sí, puedes contactar con múltiples bandas para comparar propuestas. Te recomendamos ser transparente en tus comunicaciones. Una vez alcances un acuerdo con una banda, deberías informar al resto.',
        },
      ],
    },
    {
      categoria: 'Acuerdos y Negociación',
      icon: FaHandshake,
      preguntas: [
        {
          pregunta: '¿Cómo funciona el sistema de acuerdos?',
          respuesta: 'Una vez negociados los detalles por chat, puedes formalizar el acuerdo haciendo clic en "Proponer acuerdo". Incluye el precio acordado y descripción de servicios. La banda recibirá la propuesta y podrá aceptarla o rechazarla. Una vez aceptado, el acuerdo queda registrado en la plataforma.',
        },
        {
          pregunta: '¿El acuerdo en la plataforma es vinculante legalmente?',
          respuesta: 'El acuerdo en Cadencya sirve como registro de las condiciones pactadas, pero recomendamos siempre formalizar un contrato legal por escrito. Cadencya actúa como intermediaria para facilitar la conexión, pero no es parte del contrato entre cofradía y banda.',
        },
        {
          pregunta: '¿Puedo cancelar un acuerdo ya aceptado?',
          respuesta: 'Sí, puedes rescindir un acuerdo desde "Detalles del evento" → "Rescindir acuerdo". Deberás proporcionar un motivo. La banda será notificada inmediatamente. Ten en cuenta que esto puede afectar negativamente a tu reputación en la plataforma.',
        },
      ],
    },
    {
      categoria: 'Reseñas y Valoraciones',
      icon: FaQuestionCircle,
      preguntas: [
        {
          pregunta: '¿Cuándo puedo dejar una reseña?',
          respuesta: 'Puedes dejar una reseña solo después de que el evento se haya completado y haya existido un acuerdo confirmado. Recibirás una notificación invitándote a valorar la experiencia.',
        },
        {
          pregunta: '¿Qué aspectos valoro en una banda?',
          respuesta: 'Valorarás: puntualidad, comunicación, calidad artística, profesionalidad y valoración general. También puedes dejar un comentario escrito. Sé honesto pero respetuoso, ya que las reseñas ayudan a otras cofradías.',
        },
        {
          pregunta: '¿Puedo editar o eliminar una reseña?',
          respuesta: 'Puedes editar tu reseña durante las primeras 48 horas tras publicarla. Después, solo podrás solicitar su eliminación contactando con soporte si hay un motivo justificado.',
        },
      ],
    },
  ];

  const faqsBandas = [
    {
      categoria: 'Primeros Pasos',
      icon: FaUserPlus,
      preguntas: [
        {
          pregunta: '¿Cómo me registro como banda?',
          respuesta: 'Haz clic en «Registrarse», selecciona banda y completa el formulario. Después, en Perfil, rellena los datos obligatorios: se genera un código de validación que debes enviar por mensaje directo a nuestras redes. El repertorio de partituras se gestiona en Gestión → Archivo musical, no solo en el perfil.',
        },
        {
          pregunta: '¿Qué tipo de agrupaciones pueden registrarse?',
          respuesta: 'Pueden registrarse: Bandas de Música, Bandas de Cornetas y Tambores, Agrupaciones Musicales, Charangas, Música de Capilla y otras formaciones relacionadas con música procesional.',
        },
        {
          pregunta: '¿Cómo hago mi perfil más atractivo?',
          respuesta: 'Sube fotos de calidad, completa especialidades y el repertorio de vitrina, consigue avales y mantén el calendario. Los ensayos y documentos internos están en Gestión, no en el perfil público.',
        },
      ],
    },
    {
      categoria: 'Gestión (actuaciones, ensayos, repertorio)',
      icon: FaCalendar,
      preguntas: [
        {
          pregunta: '¿Cómo paso lista en una actuación o ensayo?',
          respuesta:
            'Abre la actuación (pestaña Asistencia) o el detalle del ensayo y pulsa «Pasar lista». Verás a los músicos uno a uno en un carrusel y podrás marcar asiste / no asiste (o presente / ausente) al momento.',
        },
        {
          pregunta: '¿Cómo comparto la encuesta de asistencia por WhatsApp?',
          respuesta:
            'En la actuación, con la encuesta abierta, puedes enviar el enlace individual a cada músico o usar «Compartir encuesta al grupo»: el enlace general permite que cada músico se identifique y responda.',
        },
        {
          pregunta: '¿Cómo añado partituras al Archivo musical?',
          respuesta:
            'En Gestión → Archivo musical → «Añadir obras». Puedes subir PDFs/imágenes sueltos o seleccionar una carpeta completa. La IA analiza y separa las obras.',
        },
        {
          pregunta: '¿Qué es Tu Web?',
          respuesta:
            'En Perfil → Tu Web configuras el micrositio público de la banda (tema, secciones y animaciones). Por defecto las animaciones no desplazan el contenido al hacer scroll.',
        },
        {
          pregunta: '¿Qué diferencia hay entre grupos de ensayos y grupos de músicos?',
          respuesta:
            'Los grupos de ensayos (Gestión → Ensayos) agrupan sesiones de ensayo, repertorio y vínculo a actuaciones. Los grupos de músicos (Gestión → Mis músicos) agrupan integrantes (secciones, junta…) para convocarlos en ensayos, formaciones y encuestas.',
        },
        {
          pregunta: '¿Cómo añado músicos y formaciones?',
          respuesta:
            'Gestión → Mis músicos para el censo (plantilla y grupos de músicos). Gestión → Formaciones para las plantillas de escenario o procesión. Sin músicos no se pueden crear formaciones, ensayos ni actuaciones.',
        },
        {
          pregunta: '¿Qué es el tablón de documentos?',
          respuesta:
            'Gestión → Documentos es la biblioteca interna. Marca «En el tablón» los archivos que quieras compartir y activa una contraseña para generar el enlace público. Solo el titular puede subir.',
        },
        {
          pregunta: '¿Cómo funcionan los planes y el almacenamiento?',
          respuesta:
            'En Perfil → Suscripción ves el plan (Gratis, Estándar, Intermedio, Avanzado), el consumo de IA del ciclo y el disco ocupado frente al tope. Las subidas se cortan al llegar al límite; el disco no se reinicia cada mes.',
        },
        {
          pregunta: '¿Qué son los permisos de colaboradores?',
          respuesta:
            'El titular, en Gestión → Permisos, crea usuarios con email y contraseña y marca a qué módulos acceden (archivo, músicos, ensayos, Tu Web, etc.).',
        },
      ],
    },
    {
      categoria: 'Calendario y Disponibilidad',
      icon: FaCalendar,
      preguntas: [
        {
          pregunta: '¿Cómo gestiono mi calendario?',
          respuesta: 'Ve a "Calendario" y marca tus fechas: Disponible (días libres), Ocupado (compromisos no procesionales), o Evento (actuaciones confirmadas). Mantén tu calendario actualizado para que las cofradías vean cuándo estás disponible.',
        },
        {
          pregunta: '¿Las cofradías pueden ver mi calendario?',
          respuesta: 'Las cofradías solo ven si estás disponible o no en una fecha específica cuando buscan bandas. No ven detalles de tus otros compromisos por privacidad.',
        },
        {
          pregunta: '¿Qué pasa si confirmo una actuación fuera de la plataforma?',
          respuesta: 'Debes marcar manualmente esa fecha como "Ocupado" o "Evento" en tu calendario para evitar dobles reservas y mantener tu disponibilidad precisa.',
        },
      ],
    },
    {
      categoria: 'Recibir Propuestas',
      icon: FaComments,
      preguntas: [
        {
          pregunta: '¿Cómo recibo propuestas de cofradías?',
          respuesta: 'Las cofradías te contactarán directamente por el chat de la plataforma. Recibirás notificaciones por email y en la plataforma. Revisa regularmente tu sección "Mensajes" para no perder oportunidades.',
        },
        {
          pregunta: '¿Puedo rechazar una propuesta?',
          respuesta: 'Por supuesto. No estás obligado a aceptar ninguna propuesta. Si no te interesa, comunícalo educadamente a la cofradía lo antes posible para que puedan buscar alternativas.',
        },
        {
          pregunta: '¿Puedo proponer un precio diferente?',
          respuesta: 'Sí, las condiciones y precios son negociables. Usa el chat para discutir todos los detalles antes de que se formalice un acuerdo. Sé transparente sobre vuestros costes y requisitos.',
        },
      ],
    },
    {
      categoria: 'Acuerdos',
      icon: FaHandshake,
      preguntas: [
        {
          pregunta: '¿Cómo acepto un acuerdo?',
          respuesta: 'Cuando la cofradía te envíe una propuesta de acuerdo formal, recibirás una notificación. Revisa todos los detalles (fecha, hora, precio, condiciones) y haz clic en "Aceptar acuerdo". Si algo no está claro, pregunta antes de aceptar.',
        },
        {
          pregunta: '¿Qué pasa después de aceptar un acuerdo?',
          respuesta: 'El acuerdo queda registrado, tu calendario se actualiza automáticamente marcando esa fecha como "Evento", y tanto tú como la cofradía recibiréis confirmación. Mantén la comunicación abierta para coordinar detalles finales.',
        },
        {
          pregunta: '¿Qué hago si hay un imprevisto y no puedo cumplir?',
          respuesta: 'Contacta INMEDIATAMENTE con la cofradía por chat. Explica la situación y busca una solución (banda sustituta, nueva fecha, etc.). Si es necesario cancelar, hazlo desde "Rescindir acuerdo" explicando el motivo. Esto afectará a tu reputación.',
        },
      ],
    },
    {
      categoria: 'Reputación',
      icon: FaQuestionCircle,
      preguntas: [
        {
          pregunta: '¿Cómo funciona el sistema de reseñas?',
          respuesta: 'Después de cada evento completado, tanto tú como la cofradía podéis dejaros una reseña mutua. Las reseñas incluyen valoraciones numéricas y comentarios escritos. Tu puntuación media se muestra en tu perfil.',
        },
        {
          pregunta: '¿Puedo elegir qué reseñas mostrar?',
          respuesta: 'Puedes decidir si publicar cada reseña recibida o mantenerla privada. Las reseñas públicas son visibles para todas las cofradías y ayudan a construir tu reputación. Te recomendamos publicarlas para aumentar la confianza.',
        },
        {
          pregunta: '¿Qué son los avales?',
          respuesta: 'Los avales son recomendaciones especiales de cofradías con las que has trabajado previamente (fuera de la plataforma). Puedes solicitar avales enviando un enlace a la cofradía. Los avales validados aparecen destacados en tu perfil.',
        },
      ],
    },
  ];

  const problemasComunes = [
    {
      problema: 'No puedo iniciar sesión',
      solucion: 'Verifica que estás usando el email correcto. Si olvidaste tu contraseña, usa "¿Olvidaste tu contraseña?" en la pantalla de login. Si el problema persiste, contacta con soporte@cadencya.com',
    },
    {
      problema: 'No recibo notificaciones',
      solucion: 'Revisa el icono de campana en la cabecera (notificaciones de la plataforma) y tu carpeta de spam si esperas un email. Si el problema persiste, contacta con soporte@cadencya.com.',
    },
    {
      problema: 'No encuentro una banda/cofradía específica',
      solucion: 'Verifica los filtros de búsqueda. Prueba a ampliar el radio de proximidad o quitar filtros. Si la entidad no está registrada, puedes invitarla enviándole un enlace de registro.',
    },
    {
      problema: 'Mi perfil no se muestra en las búsquedas',
      solucion: 'Asegúrate de que tu perfil esté completo (todos los campos obligatorios) y que tu calendario de disponibilidad esté actualizado. Si el problema persiste, contacta con soporte.',
    },
    {
      problema: 'No puedo subir fotos/vídeos',
      solucion: 'Verifica el tamaño del archivo (máximo 10MB para fotos, 50MB para vídeos). Formatos admitidos: JPG, PNG para fotos; MP4, MOV para vídeos. Si el error persiste, prueba con otro navegador.',
    },
    {
      problema: 'Error al enviar mensajes',
      solucion: 'Verifica tu conexión a internet. Si el chat no carga, refresca la página. Si el problema continúa, cierra sesión y vuelve a iniciarla. Contacta con soporte si el error persiste.',
    },
  ];

  const filteredFaqsCofradias = faqsCofradias.map(cat => ({
    ...cat,
    preguntas: cat.preguntas.filter(p => 
      searchQuery === '' ||
      p.pregunta.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.respuesta.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter(cat => cat.preguntas.length > 0);

  const filteredFaqsBandas = faqsBandas.map(cat => ({
    ...cat,
    preguntas: cat.preguntas.filter(p => 
      searchQuery === '' ||
      p.pregunta.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.respuesta.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter(cat => cat.preguntas.length > 0);

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <SEO
        title="Centro de Ayuda"
        description="Preguntas frecuentes y guías de uso de Cadencya. Encuentra respuestas sobre registro, perfil, búsqueda y más."
        path="/ayuda"
      />
      {/* Header */}
      <Box bg="purple.600" color="white" py={6}>
        <Container maxW="7xl">
          <Button
            variant="ghost"
            color="white"
            _hover={{ bg: 'purple.700' }}
            mb={4}
            asChild><RouterLink to="/"><Icon asChild><FaArrowLeft /></Icon>Volver al inicio
                                    </RouterLink></Button>
          <Heading size="2xl" mb={4}>
            Centro de Ayuda
          </Heading>
          <Text fontSize="xl" maxW="3xl">
            Encuentra respuestas a las preguntas más frecuentes sobre Cadencya
          </Text>
        </Container>
      </Box>

      {/* Contenido principal */}
      <Box flex="1" bg={bgColor} py={16}>
        <Container maxW="6xl">
          {/* Buscador */}
          <Box mb={12}>
            <InputGroup size="lg">
              <InputLeftElement pointerEvents="none">
                <Icon color="gray.400" asChild><FaSearch /></Icon>
              </InputLeftElement>
              <Input
                placeholder="Busca tu pregunta..."
                bg={cardBg}
                rounded="full"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </Box>

          {/* Tabs para Bandas y Cofradías */}
          <Tabs.Root fitted variant='enclosed' colorPalette="purple">
            <Tabs.List mb={8}>
              <Tab value="0" fontSize="lg" fontWeight="600">
                <Icon mr={2} asChild><FaUserPlus /></Icon>
                Para Cofradías
              </Tab>
              <Tab value="1" fontSize="lg" fontWeight="600">
                <Icon mr={2} asChild><FaUserPlus /></Icon>
                Para Bandas
              </Tab>
              <Tab value="2" fontSize="lg" fontWeight="600">
                <Icon mr={2} asChild><FaCog /></Icon>
                Problemas Comunes
              </Tab>
            </Tabs.List>

            <TabPanels>
              {/* FAQs Cofradías */}
              <TabPanel value="0">
                <VStack gap={8} align="stretch">
                  {filteredFaqsCofradias.map((categoria, catIndex) => (
                    <Box key={catIndex}>
                      <HStack mb={4}>
                        <Icon color="purple.600" boxSize={6} asChild><categoria.icon /></Icon>
                        <Heading size="md" color="purple.600">
                          {categoria.categoria}
                        </Heading>
                      </HStack>
                      <Accordion.Root collapsible>
                        {categoria.preguntas.map((faq, faqIndex) => (
                          <Accordion.Item key={faqIndex} border="none" mb={3} value='item-0'>
                            <Accordion.ItemTrigger
                              bg={cardBg}
                              _hover={{ bg: 'purple.50' }}
                              rounded="xl"
                              p={5}
                              _expanded={{ bg: 'purple.50' }}
                            >
                              <Box flex="1" textAlign="left">
                                <Text fontWeight="600" fontSize="md">
                                  {faq.pregunta}
                                </Text>
                              </Box>
                              <Accordion.ItemIndicator />
                            </Accordion.ItemTrigger>
                            <Accordion.ItemContent bg={cardBg} p={5} rounded="xl" mt={2}><Accordion.ItemBody>
                                <Text color="gray.700">
                                  {faq.respuesta}
                                </Text>
                              </Accordion.ItemBody></Accordion.ItemContent>
                          </Accordion.Item>
                        ))}
                      </Accordion.Root>
                    </Box>
                  ))}
                  {searchQuery && filteredFaqsCofradias.length === 0 && (
                    <Text textAlign="center" color="gray.500" py={8}>
                      No encontramos respuestas para "{searchQuery}". Prueba con otros términos o contacta con soporte.
                    </Text>
                  )}
                </VStack>
              </TabPanel>

              {/* FAQs Bandas */}
              <TabPanel value="1">
                <VStack gap={8} align="stretch">
                  {filteredFaqsBandas.map((categoria, catIndex) => (
                    <Box key={catIndex}>
                      <HStack mb={4}>
                        <Icon color="purple.600" boxSize={6} asChild><categoria.icon /></Icon>
                        <Heading size="md" color="purple.600">
                          {categoria.categoria}
                        </Heading>
                      </HStack>
                      <Accordion.Root collapsible>
                        {categoria.preguntas.map((faq, faqIndex) => (
                          <Accordion.Item key={faqIndex} border="none" mb={3} value='item-1'>
                            <Accordion.ItemTrigger
                              bg={cardBg}
                              _hover={{ bg: 'purple.50' }}
                              rounded="xl"
                              p={5}
                              _expanded={{ bg: 'purple.50' }}
                            >
                              <Box flex="1" textAlign="left">
                                <Text fontWeight="600" fontSize="md">
                                  {faq.pregunta}
                                </Text>
                              </Box>
                              <Accordion.ItemIndicator />
                            </Accordion.ItemTrigger>
                            <Accordion.ItemContent bg={cardBg} p={5} rounded="xl" mt={2}><Accordion.ItemBody>
                                <Text color="gray.700">
                                  {faq.respuesta}
                                </Text>
                              </Accordion.ItemBody></Accordion.ItemContent>
                          </Accordion.Item>
                        ))}
                      </Accordion.Root>
                    </Box>
                  ))}
                  {searchQuery && filteredFaqsBandas.length === 0 && (
                    <Text textAlign="center" color="gray.500" py={8}>
                      No encontramos respuestas para "{searchQuery}". Prueba con otros términos o contacta con soporte.
                    </Text>
                  )}
                </VStack>
              </TabPanel>

              {/* Problemas Comunes */}
              <TabPanel value="2">
                <Accordion.Root collapsible>
                  {problemasComunes.map((item, index) => (
                    <Accordion.Item key={index} border="none" mb={3} value='item-2'>
                      <Accordion.ItemTrigger
                        bg={cardBg}
                        _hover={{ bg: 'purple.50' }}
                        rounded="xl"
                        p={5}
                        _expanded={{ bg: 'purple.50' }}
                      >
                        <Box flex="1" textAlign="left">
                          <Text fontWeight="600" fontSize="md">
                            {item.problema}
                          </Text>
                        </Box>
                        <Accordion.ItemIndicator />
                      </Accordion.ItemTrigger>
                      <Accordion.ItemContent bg={cardBg} p={5} rounded="xl" mt={2}><Accordion.ItemBody>
                          <Text color="gray.700">
                            {item.solucion}
                          </Text>
                        </Accordion.ItemBody></Accordion.ItemContent>
                    </Accordion.Item>
                  ))}
                </Accordion.Root>
              </TabPanel>
            </TabPanels>
          </Tabs.Root>

          {/* Contacto con soporte */}
          <Box mt={16} bg={cardBg} p={8} rounded="2xl" shadow="lg" textAlign="center">
            <Heading size="md" mb={4} color="purple.600">
              ¿No encuentras lo que buscas?
            </Heading>
            <Text color="gray.700" mb={6} maxW="2xl" mx="auto">
              Si tu pregunta no está respondida aquí, nuestro equipo de soporte estará encantado de ayudarte.
            </Text>
            <HStack gap={4} justify="center">
              <Button colorPalette="purple" size="lg" rounded="full" px={8} asChild><RouterLink to="/contacto">Contactar Soporte
                              </RouterLink></Button>
              <Button
                variant="outline"
                colorPalette="purple"
                size="lg"
                rounded="full"
                px={8}
                asChild><a href="mailto:soporte@cadencya.com">Email Directo
                              </a></Button>
            </HStack>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default Ayuda;
